/**
 * MoveToFolderDialog — folder-tree picker for moving modules or folders.
 */
import { useState, useEffect } from 'react';
import { ChevronRight, Loader2, Inbox } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { resolveIcon, resolveColor } from './folderMeta';

function buildVisibleList(tree, expanded) {
  const out = [];
  function walk(nodes, parentVisible) {
    for (const n of nodes) {
      out.push({ node: n, visible: parentVisible });
      const isOpen = expanded.has(n.folder_id);
      if (n.children && n.children.length) {
        walk(n.children, parentVisible && isOpen);
      }
    }
  }
  walk(tree, true);
  return out;
}

function PickerRow({ node, selected, onSelect, excludeIds, expanded, toggle }) {
  const Icon = resolveIcon(node.icon);
  const color = resolveColor(node.color);
  const disabled = excludeIds?.has(node.folder_id);
  const isSelected = selected === node.folder_id;
  const indent = 8 + node.depth * 14;
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = expanded.has(node.folder_id);

  return (
    <div
      className={`group flex items-center gap-1 pr-2 rounded-md text-sm
                  ${disabled ? 'opacity-40 cursor-not-allowed'
                            : isSelected ? 'bg-[var(--lumyn-content)]/10' : 'hover:bg-muted/60 cursor-pointer'}`}
      style={{ paddingLeft: indent }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (hasChildren) toggle(node.folder_id); }}
        className={`p-1 rounded hover:bg-muted ${hasChildren ? '' : 'invisible'}`}
      >
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect(node.folder_id)}
        className="flex items-center gap-2 flex-1 py-1.5 text-left min-w-0 disabled:cursor-not-allowed"
        data-testid={`picker-folder-${node.folder_id}`}
      >
        <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
        <span className="truncate">{node.name}</span>
      </button>
    </div>
  );
}

export function MoveToFolderDialog({
  open, onOpenChange, tree, title = 'Move to folder',
  currentId = null, excludeIds, allowRoot = true, onConfirm,
}) {
  const [target, setTarget] = useState(currentId);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  // Re-init target when dialog opens
  useEffect(() => {
    if (open) {
      setTarget(currentId);
      // Default: expand all top-level nodes for visibility
      setExpanded(new Set(tree.map(n => n.folder_id)));
    }
  }, [open, currentId, tree]);

  const toggle = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleOpenChange = (o) => {
    if (!submitting) onOpenChange(o);
  };

  const handle = async () => {
    setSubmitting(true);
    try {
      await onConfirm(target ?? null);
    } finally {
      setSubmitting(false);
    }
  };

  const visible = buildVisibleList(tree, expanded);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="move-to-folder-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto rounded-md border p-1 space-y-0.5">
          {allowRoot && (
            <button
              type="button"
              onClick={() => setTarget(null)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm
                          ${target === null ? 'bg-[var(--lumyn-content)]/10' : 'hover:bg-muted/60'}`}
              data-testid="picker-folder-root"
            >
              <Inbox className="w-4 h-4 text-muted-foreground" />
              <span>Root (unfiled)</span>
            </button>
          )}
          {tree.length === 0 ? (
            <div className="px-3 py-4 text-xs text-muted-foreground italic text-center">
              No folders yet
            </div>
          ) : visible.map(({ node, visible: isVisible }) => isVisible ? (
            <PickerRow
              key={node.folder_id}
              node={node}
              selected={target}
              onSelect={setTarget}
              excludeIds={excludeIds}
              expanded={expanded}
              toggle={toggle}
            />
          ) : null)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="move-cancel">
            Cancel
          </Button>
          <Button onClick={handle} disabled={submitting || target === currentId} data-testid="move-confirm">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
